<script setup>
import ChatEmptyState from './ChatEmptyState.vue';
import { l } from '@js/utils/toolbox';
import { useChatApi } from './useChatApi';

const {
	loadConversation,
	messages,
	conversationId,
	loadConversations,
	conversations,
	deleteConversation
} = useChatApi();

onMounted(async () => {
	await loadConversations();
});

const isConversationIdSet = computed(() => !!conversationId.value);
const isConversationsEmpty = computed(() => conversations.value?.length === 0);
const isMessageEmpty = computed(() => messages.value?.length === 0);

const showEmptyState = computed(() =>
	(isConversationsEmpty.value && isMessageEmpty.value)
  || (isConversationIdSet.value && isMessageEmpty.value)
);

const showRecentConversations = computed(() =>
	!isConversationsEmpty.value
  && isMessageEmpty.value
  && !isConversationIdSet.value
);

const showDisclaimer = computed(() =>
	showEmptyState.value || showRecentConversations.value
);

function handleLoadConversation (id) {
	loadConversation(id, 'recent_list');
}

async function handleDeleteConversation (id) {
	await deleteConversation(id, 'recent_list');
}
</script>

<template>
  <ChatEmptyState v-if="showEmptyState" />

  <div v-if="showRecentConversations" class="conversation-wrapper">
    <h2 class="header">
      {{ l('chat.recent_conversations') }}
    </h2>

    <ul class="conversation-list fixed-height">
      <li
        v-for="conversation in conversations"
        :key="conversation.id"
        class="conversation-item"
        :aria-label="conversation.title"
        @click="handleLoadConversation(conversation.id)"
      >
        <span class="title">{{ conversation.title }}</span>

        <span
          class="ml-auto icon icon-delete"
          @click.stop="handleDeleteConversation(conversation.id)"
        />
      </li>
    </ul>
  </div>

  <ChatDisclaimer v-if="showDisclaimer" />
</template>

<style scoped lang="scss">
.conversation-wrapper {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  text-align: center;
  margin-top: auto;
  max-width: 100%;
  width: 100%;

  @include breakpoint_up('md') {
    width: 420px;
  }
}

.header {
  text-align: center;
  font-size: 1.8rem;
  line-height: 1.6rem;
  color: $color-blue-800;
}

/* Added fixed height and overflow */
.fixed-height {
  max-height: 250px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 0.5rem;
}

.conversation-list {
  margin: 0 auto;
  padding: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.conversation-item {
    overflow: hidden;
    display: flex;
    padding: .5rem;
    min-height: 4.5rem;
    align-items: center;
    border-radius: .4rem;
    border: .1rem solid $color-grey-200;
    white-space: nowrap;
    text-decoration: none;
    cursor: pointer;

    .title {
      display: inline-block;
      max-width: calc(100% - 4rem);
      text-align: left;
      text-overflow: ellipsis;
      padding-left: .5rem;
    }

    .icon-delete {
      padding: .5rem;
      border-radius: .4rem;
      border: .1px solid $color-blue-700;
      font-size: 2.2rem;

      &:hover {
        background-color: $color-red-100;
        border: .1px solid $black;
        color: $black;
      }
    }

  &:hover {
    border: .1rem solid $color-blue-500;
    background-color: $color-blue-000;
  }

  &:active {
    transform: scale(0.98);
  }
}

.title {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: 1.6rem;
  color: $black;
  font-weight: 400;
}
</style>
